import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { ArcElement, Chart, Tooltip as ChartTooltip } from "chart.js";
import { Doughnut } from "react-chartjs-2";
import { Helmet, HelmetProvider } from "react-helmet-async";
import {
  FiAlertCircle,
  FiArchive,
  FiArrowRight,
  FiCalendar,
  FiCheckSquare,
  FiDownload,
  FiFilePlus,
  FiInfo,
  FiTrendingDown,
  FiTrendingUp,
} from "react-icons/fi";
import { MdBalance } from "react-icons/md";
import { useNavigate } from "react-router-dom";
import Navbar from "./Navbar";
import { LanguageContext } from "./LanguageContext";
import { useCapabilities } from "./contexts/CapabilitiesContext";
import HierarchyFilterBar from "./components/report/HierarchyFilterBar";
import { getDistricts } from "./services/districtService";
import {
  REPORT_FILTER_OPTION_STORAGE_KEY,
  REPORT_HIERARCHY_SCOPE_STORAGE_KEY,
  fetchAdminDashboardSummary,
  fetchCasePropertyCount,
  fetchCrimeBranchRecords,
  fetchEntryTypeCounts,
  fetchFilteredCrimeRecords,
  fetchPersonalDashboardSummary,
  fetchPersonalRecords,
  normalizeDateFilterOption,
} from "./services/reportService";
import { getStations } from "./services/stationService";
import { getSubDivisions } from "./services/subDivisionService";
import { getScopedUsers } from "./services/userLookupService";
import {
  buildScopeFromHierarchySelection,
  ensureFixedOption,
  getHierarchyScopeAccess,
  resolveSelectedOptionLabel,
} from "./utils/hierarchyScope";
import enTranslations from "./translations/en.json";
import guTranslations from "./translations/gu.json";
import hinTranslations from "./translations/hi.json";
import mrTranslations from "./translations/mr.json";
import { UI_TRANSLATIONS } from "./translations/ui";
import "./Dashbord.css";

Chart.register(ArcElement, ChartTooltip);

const TYPE_COLORS = [
  "#0d4f91",
  "#168ca2",
  "#2b8a68",
  "#7c4fc4",
  "#db8b16",
  "#c44d59",
  "#61758f",
];

const STATUS_COLORS = [
  "#164f93",
  "#1d77b4",
  "#1b9a9b",
  "#2e9870",
  "#73a65a",
  "#d29a45",
  "#d66b73",
  "#7e8fa8",
];

const ENTRY_TYPE_COLORS = [
  "#164f93",
  "#168ca2",
  "#2e9870",
  "#d29a45",
  "#c44d59",
  "#7c4fc4",
  "#61758f",
  "#bd690a",
];
const VEHICLE_ENTRY_TYPE_CATEGORY = "Vehicle";

const DATE_FILTER_OPTIONS = [
  { value: "last_week", labelKey: "last_week" },
  { value: "last_month", labelKey: "last_month_period" },
  { value: "last_three_months", labelKey: "last_three_months" },
  { value: "last_six_months", labelKey: "last_six_months" },
  { value: "custom", labelKey: "custom_date" },
];

const translations = {
  en: enTranslations,
  gu: guTranslations,
  hi: hinTranslations,
  mr: mrTranslations,
};

const asArray = (value) => (Array.isArray(value) ? value : []);
const asObject = (value) =>
  value && typeof value === "object" && !Array.isArray(value) ? value : {};
const asNumber = (value, fallback = 0) => {
  const parsedValue = Number(value);
  return Number.isFinite(parsedValue) ? parsedValue : fallback;
};
const firstNumber = (...values) => {
  for (const value of values) {
    if (value !== undefined && value !== null && value !== "") {
      const parsedValue = Number(value);
      if (Number.isFinite(parsedValue)) {
        return parsedValue;
      }
    }
  }

  return 0;
};
const normalizeText = (value) => String(value ?? "").trim();
const formatCount = (value) =>
  new Intl.NumberFormat("en-IN", { maximumFractionDigits: 0 }).format(
    asNumber(value)
  );
const formatPercentage = (value) => {
  const roundedValue = Math.round(asNumber(value) * 10) / 10;

  return Number.isInteger(roundedValue)
    ? roundedValue.toFixed(0)
    : roundedValue.toFixed(1);
};

const formatDate = (value, options = {}, locale = "en-GB") => {
  if (!value) {
    return "—";
  }

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return normalizeText(value) || "—";
  }

  return new Intl.DateTimeFormat(locale, {
    day: "2-digit",
    month: "short",
    year: "numeric",
    ...options,
  }).format(date);
};

const formatDateTime = (value, locale = "en-GB") => {
  if (!value) {
    return { date: "—", time: "" };
  }

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return { date: normalizeText(value) || "—", time: "" };
  }

  return {
    date: formatDate(date, {}, locale),
    time: new Intl.DateTimeFormat(locale, {
      hour: "2-digit",
      hour12: false,
      minute: "2-digit",
    }).format(date),
  };
};

const getMetricRows = (
  source,
  colors,
  limit = 7,
  overflowLabel = "Other"
) => {
  const rows = Object.entries(asObject(source))
    .map(([label, value]) => ({
      label: normalizeText(label) || "Not specified",
      value: asNumber(value),
    }))
    .sort((left, right) => right.value - left.value)
    .map((metric, index) => ({
      ...metric,
      color: colors[index % colors.length],
    }));

  if (!limit || rows.length <= limit) {
    return rows;
  }

  const visibleRows = rows.slice(0, Math.max(1, limit - 1));
  const overflowValue = rows
    .slice(visibleRows.length)
    .reduce((total, metric) => total + metric.value, 0);

  return [
    ...visibleRows,
    {
      aggregate: true,
      color: colors[visibleRows.length % colors.length],
      label: overflowLabel,
      value: overflowValue,
    },
  ];
};

const getMetricTotal = (rows) =>
  rows.reduce((total, metric) => total + asNumber(metric.value), 0);

const isCurrentMonth = (value) => {
  if (!value) {
    return false;
  }

  const date = new Date(value);
  const now = new Date();

  return (
    !Number.isNaN(date.getTime()) &&
    date.getFullYear() === now.getFullYear() &&
    date.getMonth() === now.getMonth()
  );
};

const isDisposedStatus = (value) => {
  const normalizedStatus = normalizeText(value).toLowerCase();
  return (
    normalizedStatus.includes("disposed") ||
    normalizedStatus.includes("auction") ||
    normalizedStatus.includes("destroyed")
  );
};

const getRecordDate = (record) =>
  record?.updated_at ||
  record?.status_updated_at ||
  record?.created_at ||
  record?.date_of_seizure;

const getRecordReference = (record) =>
  normalizeText(
    record?.guna_number ||
      record?.fir_no ||
      record?.crime_number ||
      record?.case_number
  ) || "Case reference not entered";

const getRecordPropertyId = (record) =>
  normalizeText(record?.serial_number || record?.property_id || record?.id) ||
  "—";

const getRecordUser = (record) =>
  normalizeText(
    record?.updated_by_name ||
      record?.updated_by ||
      record?.user_name ||
      record?.seized_by_name
  ) || "—";

const getPercentageChange = (currentValue, previousValue) => {
  const previous = asNumber(previousValue);

  if (previous <= 0) {
    return null;
  }

  return ((asNumber(currentValue) - previous) / previous) * 100;
};

const escapeCsvCell = (value) =>
  `"${String(value ?? "").replace(/"/g, '""')}"`;

function InfoHint({ label }) {
  return (
    <span aria-label={label} className="dashboard-info-hint" title={label}>
      <FiInfo />
    </span>
  );
}

function KpiCard({
  accent,
  context,
  icon: Icon,
  info,
  label,
  lastMonthLabel = "Last month",
  previousValue,
  value,
}) {
  const change = getPercentageChange(value, previousValue);
  const hasTrend = change !== null;
  const trendUp = hasTrend && change >= 0;

  return (
    <article className="dashboard-kpi">
      <div className="dashboard-kpi__content">
        <div className="dashboard-kpi__label">
          <span>{label}</span>
          <InfoHint label={info || label} />
        </div>
        <strong className="dashboard-kpi__value">{formatCount(value)}</strong>
        <div className="dashboard-kpi__context">
          {previousValue !== undefined && previousValue !== null ? (
            <span>
              {lastMonthLabel}: {formatCount(previousValue)}
            </span>
          ) : (
            <span>{context}</span>
          )}
          {hasTrend ? (
            <span
              className={`dashboard-kpi__trend ${
                trendUp
                  ? "dashboard-kpi__trend--up"
                  : "dashboard-kpi__trend--down"
              }`}
            >
              {trendUp ? <FiTrendingUp /> : <FiTrendingDown />}
              {Math.abs(change).toFixed(1)}%
            </span>
          ) : null}
        </div>
      </div>
      <span
        aria-hidden="true"
        className="dashboard-kpi__icon"
        style={{
          backgroundColor: accent.background,
          color: accent.foreground,
        }}
      >
        <Icon />
      </span>
    </article>
  );
}

function PanelHeader({ action, actionLabel, info, title }) {
  return (
    <div className="dashboard-panel__header">
      <div className="dashboard-panel__title">
        <h2>{title}</h2>
        {info ? <InfoHint label={info} /> : null}
      </div>
      {action && actionLabel ? (
        <button className="dashboard-panel__link" onClick={action} type="button">
          {actionLabel}
          <FiArrowRight />
        </button>
      ) : null}
    </div>
  );
}

function EmptyPanel({ children }) {
  return (
    <div className="dashboard-empty">
      <FiAlertCircle />
      <span>{children}</span>
    </div>
  );
}

function DonutPanel({
  action,
  actionLabel,
  centerLabel = "Total",
  info,
  metrics,
  onMetricClick,
  title,
  total,
  variant = "",
}) {
  const populatedMetrics = metrics.filter((metric) => metric.value > 0);
  const chartMetrics = populatedMetrics.length ? populatedMetrics : metrics;
  const hasData = populatedMetrics.length > 0;
  const chartTotal = getMetricTotal(metrics) || total;
  const chartData = {
    labels: chartMetrics.map((metric) => metric.label),
    datasets: [
      {
        backgroundColor: chartMetrics.map((metric) => metric.color),
        borderWidth: 0,
        data: chartMetrics.map((metric) => metric.value),
        hoverBorderWidth: 0,
        hoverOffset: 2,
        spacing: 0,
      },
    ],
  };
  const chartOptions = {
    circumference: 360,
    cutout: "72%",
    elements: {
      arc: {
        borderWidth: 0,
      },
    },
    layout: {
      padding: 2,
    },
    maintainAspectRatio: false,
    plugins: {
      legend: { display: false },
      tooltip: {
        bodyFont: {
          size: 9,
        },
        callbacks: {
          title: () => "",
          label: (context) =>
            `${context.label}: ${formatCount(context.raw || 0)} (${
              chartTotal > 0
                ? formatPercentage(
                    (asNumber(context.raw) / chartTotal) * 100
                  )
                : "0"
            }%)`,
        },
        displayColors: false,
        padding: 8,
      },
    },
    rotation: -90,
    responsive: true,
  };
  const centerTextPlugin = {
    id: "dashboardDonutCenterText",
    afterDatasetsDraw(chart) {
      const firstArc = chart.getDatasetMeta(0)?.data?.[0];

      if (!firstArc) {
        return;
      }

      const { ctx } = chart;
      const centerX = firstArc.x;
      const centerY = firstArc.y;
      const valueFontSize = variant === "type" ? 24 : 22;
      const valueLineHeight = valueFontSize;
      const labelFontSize = 10;
      const labelLineHeight = 12;
      const lineGap = 5;
      const groupHeight = valueLineHeight + lineGap + labelLineHeight;
      const groupTop = centerY - groupHeight / 2;

      ctx.save();
      ctx.textAlign = "center";
      ctx.textBaseline = "middle";
      ctx.fillStyle = variant === "type" ? "#153f30" : "#13233a";
      ctx.font = `700 ${valueFontSize}px Inter, system-ui, sans-serif`;
      ctx.fillText(
        formatCount(chartTotal),
        centerX,
        groupTop + valueLineHeight / 2
      );

      ctx.fillStyle = variant === "type" ? "#648073" : "#66768a";
      ctx.font = `600 ${labelFontSize}px Inter, system-ui, sans-serif`;
      ctx.fillText(
        centerLabel,
        centerX,
        groupTop + valueLineHeight + lineGap + labelLineHeight / 2
      );
      ctx.restore();
    },
  };

  return (
    <section
      className={`dashboard-panel dashboard-panel--chart ${
        variant ? `dashboard-panel--${variant}` : ""
      }`.trim()}
    >
      <PanelHeader
        action={action}
        actionLabel={actionLabel}
        info={info}
        title={title}
      />
      {hasData ? (
        <div className="dashboard-donut-layout">
          <div className="dashboard-donut">
            <Doughnut
              aria-label={`${formatCount(chartTotal)} ${centerLabel}`}
              data={chartData}
              options={chartOptions}
              plugins={[centerTextPlugin]}
              role="img"
              className="dashboard_donut_chart_new"
            />
          </div>
          <div className="dashboard-legend">
            {metrics.map((metric, metricIndex) => {
              const percentage =
                chartTotal > 0 ? (metric.value / chartTotal) * 100 : 0;

              return (
                <button
                  className="dashboard-legend__row"
                  disabled={
                    !onMetricClick ||
                    metric.aggregate ||
                    metric.value <= 0
                  }
                  key={`${metric.label}-${metricIndex}`}
                  onClick={() => onMetricClick?.(metric)}
                  style={
                    variant === "type"
                      ? {
                          "--dashboard-metric-color": metric.color,
                          "--dashboard-metric-share": `${Math.min(
                            percentage,
                            100
                          )}%`,
                        }
                      : undefined
                  }
                  type="button"
                >
                  <span
                    aria-hidden="true"
                    className="dashboard-legend__dot"
                    style={{ backgroundColor: metric.color }}
                  />
                  <span className="dashboard-legend__label">
                    {metric.label}
                  </span>
                  <strong>{formatCount(metric.value)}</strong>
                  <span className="dashboard-legend__percent">
                    ({formatPercentage(percentage)}%)
                  </span>
                </button>
              );
            })}
          </div>
        </div>
      ) : (
        <EmptyPanel>No data is available for the selected scope.</EmptyPanel>
      )}
    </section>
  );
}

function CompactTable({
  action,
  actionLabel,
  columns,
  emptyMessage,
  rows,
  title,
  variant = "",
}) {
  return (
    <section className="dashboard-panel dashboard-panel--table">
      <PanelHeader action={action} actionLabel={actionLabel} title={title} />
      {rows.length ? (
        <div className="dashboard-table-wrap">
          <table
            className={`dashboard-table ${
              variant ? `dashboard-table--${variant}` : ""
            }`.trim()}
          >
            <thead>
              <tr>
                {columns.map((column) => (
                  <th
                    className={`dashboard-table__column dashboard-table__column--${column.key}`}
                    key={column.key}
                    scope="col"
                  >
                    {column.label}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map((row, rowIndex) => (
                <tr key={row.key || rowIndex}>
                  {columns.map((column) => (
                    <td
                      className={`dashboard-table__column dashboard-table__column--${column.key}`}
                      key={column.key}
                    >
                      {column.render
                        ? column.render(row, rowIndex)
                        : row[column.key]}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <EmptyPanel>{emptyMessage}</EmptyPanel>
      )}
    </section>
  );
}

function EntryTypeCardGrid({ entries, onEntryClick, title }) {
  return (
    <section className="dashboard-panel dashboard-entry-types">
      <PanelHeader title={title} />
      {entries.length ? (
        <div className="dashboard-entry-type-grid">
          {entries.map((entry) => {
            const entryCode = normalizeText(entry.codeLabel);
            const entryLabel = entryCode
              ? `${entry.label} (${entryCode})`
              : entry.label;

            return (
              <button
                className="dashboard-entry-type-card"
                key={entry.key}
                onClick={() => onEntryClick(entry)}
                type="button"
              >
                {/* <span
                  aria-hidden="true"
                  className="dashboard-entry-type-card__accent"
                  style={{ backgroundColor: entry.color }}
                /> */}
                <span className="dashboard-entry-type-card__content">
                  <span className="dashboard-entry-type-card__label">
                    {entryLabel}
                  </span>
                  <strong style={{ color: entry.color }}>
                    {formatCount(entry.value)}
                  </strong>
                  <span
                    aria-hidden="true"
                    className="dashboard-entry-type-card__code"
                  />
                </span>
              </button>
            );
          })}
        </div>
      ) : (
        <EmptyPanel>No entry type data is available.</EmptyPanel>
      )}
    </section>
  );
}

export default function Dashbord() {
  const navigate = useNavigate();
  const { selectedLanguage } = useContext(LanguageContext);
  const {
    can,
    capabilities,
    loading: capabilitiesLoading,
    user,
  } = useCapabilities();

  const [reportData, setReportData] = useState({});
  const [statusData, setStatusData] = useState({});
  const [casePropertySummary, setCasePropertySummary] = useState({});
  const [entryTypeCounts, setEntryTypeCounts] = useState([]);
  const [vehicleEntryTypeCounts, setVehicleEntryTypeCounts] = useState([]);
  const [records, setRecords] = useState([]);
  const [dashboardLoading, setDashboardLoading] = useState(true);
  const [dashboardError, setDashboardError] = useState("");

  const [districtOptions, setDistrictOptions] = useState([]);
  const [subDivisionOptions, setSubDivisionOptions] = useState([]);
  const [stationOptions, setStationOptions] = useState([]);
  const [userOptions, setUserOptions] = useState([]);
  const [districtId, setDistrictId] = useState("");
  const [subDivisionId, setSubDivisionId] = useState("");
  const [stationId, setStationId] = useState("");
  const [userId, setUserId] = useState("");
  const [filterOption, setFilterOption] = useState("");
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);

  const translatedText = useCallback(
    (key) =>
      UI_TRANSLATIONS[selectedLanguage]?.[key] ||
      translations[selectedLanguage]?.[key] ||
      UI_TRANSLATIONS.en[key] ||
      enTranslations[key] ||
      key,
    [selectedLanguage]
  );

  const isCapabilitiesReady =
    !capabilitiesLoading && capabilities !== null;
  const canReadAdminReports =
    isCapabilitiesReady && can("reports", "read_admin");
  const canReadReports =
    canReadAdminReports || (isCapabilitiesReady && can("reports", "read"));
  const {
    canSelectDistrict,
    canSelectStation,
    canSelectSubDivision,
    canUseAdminHierarchy,
    fixedDistrictId,
    fixedStationId,
    fixedSubDivisionId,
    normalizeScopeId,
    showDistrictField,
    showStationField,
    showSubDivisionField,
  } = getHierarchyScopeAccess({
    canReadAdminReports,
    capabilitiesReady: isCapabilitiesReady,
    user,
  });

  const getReportScope = useCallback(
    () =>
      buildScopeFromHierarchySelection({
        canSelectDistrict,
        canSelectStation,
        canSelectSubDivision,
        districtId,
        fixedDistrictId,
        fixedStationId,
        fixedSubDivisionId,
        includeUser: true,
        normalizeValue: normalizeScopeId,
        stationId,
        subDivisionId,
        userId,
      }),
    [
      canSelectDistrict,
      canSelectStation,
      canSelectSubDivision,
      districtId,
      fixedDistrictId,
      fixedStationId,
      fixedSubDivisionId,
      normalizeScopeId,
      stationId,
      subDivisionId,
      userId,
    ]
  );

  useEffect(() => {
    const storedScope = localStorage.getItem(
      REPORT_HIERARCHY_SCOPE_STORAGE_KEY
    );
    const storedFilterOption = localStorage.getItem(
      REPORT_FILTER_OPTION_STORAGE_KEY
    );

    if (storedScope) {
      try {
        const parsedScope = JSON.parse(storedScope);
        setDistrictId(parsedScope?.districtId || "");
        setSubDivisionId(parsedScope?.subDivisionId || "");
        setStationId(parsedScope?.stationId || "");
        setUserId(parsedScope?.userId || "");
      } catch (error) {
        console.error("Unable to restore dashboard scope:", error);
      }
    }

    if (storedFilterOption) {
      setFilterOption(normalizeDateFilterOption(storedFilterOption));
    }
  }, []);

  useEffect(() => {
    localStorage.setItem(
      REPORT_HIERARCHY_SCOPE_STORAGE_KEY,
      JSON.stringify({ districtId, stationId, subDivisionId, userId })
    );

    if (filterOption) {
      localStorage.setItem(
        REPORT_FILTER_OPTION_STORAGE_KEY,
        filterOption
      );
    } else {
      localStorage.removeItem(REPORT_FILTER_OPTION_STORAGE_KEY);
    }
  }, [districtId, filterOption, stationId, subDivisionId, userId]);

  useEffect(() => {
    if (!canUseAdminHierarchy) {
      setDistrictOptions([]);
      setSubDivisionOptions([]);
      setStationOptions([]);
      setUserOptions([]);
      return undefined;
    }

    let active = true;
    const loadFilterOptions = async () => {
      const selectedDistrictId = districtId || fixedDistrictId;
      const selectedSubDivisionId = subDivisionId || fixedSubDivisionId;
      const selectedStationId = stationId || fixedStationId;
      const userParams = selectedStationId
        ? {
            station_id: selectedStationId,
            ...(selectedSubDivisionId
              ? { sub_division_id: selectedSubDivisionId }
              : {}),
            ...(selectedDistrictId ? { district_id: selectedDistrictId } : {}),
          }
        : selectedSubDivisionId
        ? {
            sub_division_id: selectedSubDivisionId,
            ...(selectedDistrictId ? { district_id: selectedDistrictId } : {}),
          }
        : selectedDistrictId
        ? { district_id: selectedDistrictId }
        : {};
      const [districtResult, subDivisionResult, stationResult, userResult] =
        await Promise.allSettled([
          canSelectDistrict ? getDistricts() : Promise.resolve([]),
          canSelectSubDivision
            ? getSubDivisions(
                selectedDistrictId
                  ? { district_id: selectedDistrictId }
                  : {}
              )
            : Promise.resolve([]),
          canSelectStation || fixedStationId
            ? getStations(
                selectedSubDivisionId
                  ? {
                      sub_division_id: selectedSubDivisionId,
                      ...(selectedDistrictId
                        ? { district_id: selectedDistrictId }
                        : {}),
                    }
                  : selectedDistrictId
                  ? { district_id: selectedDistrictId }
                  : {}
              )
            : Promise.resolve([]),
          canUseAdminHierarchy
            ? getScopedUsers(userParams)
            : Promise.resolve([]),
        ]);

      if (!active) {
        return;
      }

      setDistrictOptions(
        districtResult.status === "fulfilled"
          ? asArray(districtResult.value)
          : []
      );
      setSubDivisionOptions(
        subDivisionResult.status === "fulfilled"
          ? asArray(subDivisionResult.value)
          : []
      );
      setStationOptions(
        stationResult.status === "fulfilled"
          ? asArray(stationResult.value)
          : []
      );
      setUserOptions(
        userResult.status === "fulfilled" ? asArray(userResult.value) : []
      );
    };

    loadFilterOptions();
    return () => {
      active = false;
    };
  }, [
    canSelectDistrict,
    canSelectStation,
    canSelectSubDivision,
    canUseAdminHierarchy,
    districtId,
    fixedDistrictId,
    fixedStationId,
    fixedSubDivisionId,
    stationId,
    subDivisionId,
  ]);

  useEffect(() => {
    if (!isCapabilitiesReady) {
      return undefined;
    }

    if (
      canUseAdminHierarchy &&
      filterOption === "custom" &&
      (!startDate || !endDate)
    ) {
      return undefined;
    }

    let active = true;
    const loadDashboard = async () => {
      setDashboardLoading(true);
      setDashboardError("");

      const scope = getReportScope();
      const filters = { endDate, filterOption, startDate };
      const hasReportFilter =
        Boolean(normalizeDateFilterOption(filterOption)) &&
        (normalizeDateFilterOption(filterOption) !== "custom" ||
          (startDate && endDate));

      const [
        summaryResult,
        countResult,
        recordsResult,
        entryTypeResult,
        vehicleEntryTypeResult,
      ] =
        await Promise.allSettled(
          canUseAdminHierarchy
            ? [
                fetchAdminDashboardSummary(scope, filters),
                fetchCasePropertyCount(scope),
                hasReportFilter
                  ? fetchFilteredCrimeRecords(scope, filters)
                  : fetchCrimeBranchRecords(scope),
                fetchEntryTypeCounts({
                  filters,
                  lang: selectedLanguage,
                  scope,
                }),
                fetchEntryTypeCounts({
                  category: VEHICLE_ENTRY_TYPE_CATEGORY,
                  filters,
                  lang: selectedLanguage,
                  scope,
                }),
              ]
            : [
                fetchPersonalDashboardSummary(),
                Promise.resolve({}),
                fetchPersonalRecords(),
                canReadReports
                  ? fetchEntryTypeCounts({
                      filters,
                      lang: selectedLanguage,
                      scope,
                    })
                  : Promise.resolve([]),
                canReadReports
                  ? fetchEntryTypeCounts({
                      category: VEHICLE_ENTRY_TYPE_CATEGORY,
                      filters,
                      lang: selectedLanguage,
                      scope,
                    })
                  : Promise.resolve([]),
              ]
        );

      if (!active) {
        return;
      }

      if (summaryResult.status === "fulfilled") {
        setReportData(summaryResult.value?.reportData || {});
        setStatusData(summaryResult.value?.statusData || {});
      } else {
        setReportData({});
        setStatusData({});
      }

      setCasePropertySummary(
        countResult.status === "fulfilled" ? countResult.value || {} : {}
      );
      setRecords(
        recordsResult.status === "fulfilled"
          ? asArray(recordsResult.value)
          : []
      );
      setEntryTypeCounts(
        entryTypeResult.status === "fulfilled"
          ? asArray(entryTypeResult.value)
          : []
      );
      setVehicleEntryTypeCounts(
        vehicleEntryTypeResult.status === "fulfilled"
          ? asArray(vehicleEntryTypeResult.value)
          : []
      );

      const failedResult = [
        summaryResult,
        countResult,
        recordsResult,
        entryTypeResult,
        vehicleEntryTypeResult,
      ].find((result) => result.status === "rejected");

      if (failedResult) {
        setDashboardError(translatedText("dashboard_partial_error"));
      }

      setDashboardLoading(false);
    };

    loadDashboard();
    return () => {
      active = false;
    };
  }, [
    canUseAdminHierarchy,
    canReadReports,
    endDate,
    filterOption,
    getReportScope,
    isCapabilitiesReady,
    selectedLanguage,
    startDate,
    translatedText,
  ]);

  const districtLabel = resolveSelectedOptionLabel(
    districtOptions,
    fixedDistrictId,
    translatedText("assigned_district")
  );
  const subDivisionLabel = resolveSelectedOptionLabel(
    subDivisionOptions,
    fixedSubDivisionId,
    translatedText("assigned_sub_division")
  );
  const stationLabel = resolveSelectedOptionLabel(
    stationOptions,
    fixedStationId,
    translatedText("assigned_police_station")
  );
  const districtFilterOptions = canSelectDistrict
    ? districtOptions
    : ensureFixedOption(districtOptions, fixedDistrictId, districtLabel);
  const subDivisionFilterOptions = canSelectSubDivision
    ? subDivisionOptions
    : ensureFixedOption(
        subDivisionOptions,
        fixedSubDivisionId,
        subDivisionLabel
      );
  const stationFilterOptions = canSelectStation
    ? stationOptions
    : ensureFixedOption(stationOptions, fixedStationId, stationLabel);

  const typeMetrics = useMemo(
    () =>
      getMetricRows(
        reportData.category_counts,
        TYPE_COLORS,
        null,
        "Other property types"
      ).map((metric) => ({
        ...metric,
        sourceLabel: metric.label,
      })),
    [reportData.category_counts]
  );
  const statusMetrics = useMemo(
    () =>
      getMetricRows(
        statusData,
        STATUS_COLORS,
        8,
        "Other custody statuses"
      ).map((metric) => ({
        ...metric,
        sourceLabel: metric.label,
      })),
    [statusData]
  );
  const reasonMetrics = useMemo(
    () =>
      getMetricRows(
        reportData.category_counts_reasons,
        STATUS_COLORS.slice().reverse(),
        5,
        "Other pending reasons"
      ).map((metric) => ({
        ...metric,
        sourceLabel: metric.label,
      })),
    [reportData.category_counts_reasons]
  );
  const entryTypeMetrics = useMemo(
    () =>
      asArray(entryTypeCounts).map((entry, index) => {
        const code = normalizeText(entry?.code);
        const label = normalizeText(entry?.label) || "Not specified";

        return {
          code,
          codeLabel: code || "Not specified",
          color: ENTRY_TYPE_COLORS[index % ENTRY_TYPE_COLORS.length],
          key: code || entry?.records_url || `entry-type-${index}`,
          label,
          recordsUrl: entry?.records_url,
          sourceLabel: label,
          value: asNumber(entry?.count),
        };
      }),
    [entryTypeCounts]
  );
  const vehicleEntryTypeMetrics = useMemo(
    () =>
      asArray(vehicleEntryTypeCounts).map((entry, index) => {
        const code = normalizeText(entry?.code);
        const label = normalizeText(entry?.label) || "Not specified";

        return {
          category: VEHICLE_ENTRY_TYPE_CATEGORY,
          code,
          codeLabel: code || "Not specified",
          color: ENTRY_TYPE_COLORS[index % ENTRY_TYPE_COLORS.length],
          key: code || entry?.records_url || `vehicle-entry-type-${index}`,
          label,
          recordsUrl: entry?.records_url,
          sourceLabel: label,
          value: asNumber(entry?.count),
        };
      }),
    [vehicleEntryTypeCounts]
  );

  const typeTotal = getMetricTotal(typeMetrics);
  const statusTotal = getMetricTotal(statusMetrics);
  const totalRecords = firstNumber(
    reportData.total_data_entries,
    records.length || undefined,
    typeTotal,
    statusTotal
  );
  const addedThisMonth = firstNumber(
    casePropertySummary.total_case_properties_current_month,
    records.filter((record) => isCurrentMonth(record.created_at)).length
  );
  const disposedThisMonth = firstNumber(
    casePropertySummary.total_disposed_current_month,
    records.filter(
      (record) =>
        isDisposedStatus(record.status_of_muddamaal) &&
        isCurrentMonth(getRecordDate(record))
    ).length
  );
  const totalDisposed = firstNumber(
    reportData.total_disposed_or_auction,
    records.filter((record) =>
      isDisposedStatus(record.status_of_muddamaal)
    ).length
  );
  const balanceProperties = firstNumber(
    casePropertySummary.balance_case_properties,
    Math.max(totalRecords - totalDisposed, 0)
  );
  const lastMonthTotal =
    casePropertySummary.total_case_properties_till_last_month;

  const upcomingCourtDates = useMemo(() => {
    const startOfToday = new Date();
    startOfToday.setHours(0, 0, 0, 0);

    return records
      .map((record) => {
        const rawDate = record.next_court_date || record.chart_sheet_date;
        const courtDate = rawDate ? new Date(rawDate) : null;

        return {
          courtDate,
          date: rawDate,
          description:
            normalizeText(record.pendency_reason || record.description) ||
            "No additional details",
          key: record.id || `${getRecordPropertyId(record)}-${rawDate}`,
          propertyId: getRecordPropertyId(record),
          reference: getRecordReference(record),
          type: record.next_court_date ? "Hearing" : "Charge sheet",
          typeKey: record.next_court_date ? "hearing" : "charge_sheet",
        };
      })
      .filter(
        (item) =>
          item.courtDate &&
          !Number.isNaN(item.courtDate.getTime()) &&
          item.courtDate >= startOfToday
      )
      .sort((left, right) => left.courtDate - right.courtDate)
      .slice(0, 4);
  }, [records]);

  const recentMovements = useMemo(
    () =>
      [...records]
        .sort(
          (left, right) =>
            new Date(getRecordDate(right) || 0) -
            new Date(getRecordDate(left) || 0)
        )
        .slice(0, 5)
        .map((record) => ({
          action:
            normalizeText(record.status_of_muddamaal) ||
            "Status not entered",
          by: getRecordUser(record),
          date: getRecordDate(record),
          key: record.id || getRecordPropertyId(record),
          propertyId: getRecordPropertyId(record),
        })),
    [records]
  );

  const handleMetricClick = (categoryType, metric) => {
    if (!canReadAdminReports || metric.aggregate || metric.value <= 0) {
      return;
    }

    const encodedCategory = encodeURIComponent(
      metric.sourceLabel || metric.label
    );
    navigate(`/report-page/${categoryType}/${encodedCategory}`, {
      state: {
        category: encodedCategory,
        categoryType,
        endDate:
          filterOption === "custom" && endDate
            ? endDate.toISOString()
            : "",
        filterOption,
        scope: getReportScope(),
        startDate:
          filterOption === "custom" && startDate
            ? startDate.toISOString()
            : "",
      },
    });
  };

  const handleEntryTypeClick = (metric) => {
    if (!canReadReports) {
      return;
    }

    const entryTypeCode = metric.code || "none";
    const encodedEntryType = encodeURIComponent(entryTypeCode);

    navigate(`/report-page/entry_type/${encodedEntryType}`, {
      state: {
        category: encodedEntryType,
        categoryFilter: metric.category || "",
        categoryLabel: metric.label,
        categoryType: "entry_type",
        endDate:
          filterOption === "custom" && endDate
            ? endDate.toISOString()
            : "",
        entryTypeCode,
        filterOption,
        recordsUrl: metric.recordsUrl,
        scope: getReportScope(),
        startDate:
          filterOption === "custom" && startDate
            ? startDate.toISOString()
            : "",
      },
    });
  };

  const handleExport = () => {
    const rows = records.length
      ? [
          [
            translatedText("property_id"),
            translatedText("case_reference"),
            translatedText("muddamaal_type"),
            translatedText("custody_status"),
            translatedText("court_date"),
            translatedText("updated"),
          ],
          ...records.map((record) => [
            getRecordPropertyId(record),
            getRecordReference(record),
            normalizeText(record.muddamaal_type),
            normalizeText(record.status_of_muddamaal),
            formatDate(record.next_court_date, {}, "en-GB"),
            formatDate(getRecordDate(record), {}, "en-GB"),
          ]),
        ]
      : [
          [translatedText("metric"), translatedText("count")],
          [translatedText("total_case_properties"), totalRecords],
          [translatedText("added_this_month"), addedThisMonth],
          [translatedText("disposed_this_month"), disposedThisMonth],
          [translatedText("balance_case_properties"), balanceProperties],
        ];
    const csv = rows
      .map((row) => row.map(escapeCsvCell).join(","))
      .join("\n");
    const blob = new Blob([`\uFEFF${csv}`], {
      type: "text/csv;charset=utf-8",
    });
    const downloadUrl = URL.createObjectURL(blob);
    const downloadLink = document.createElement("a");
    downloadLink.href = downloadUrl;
    downloadLink.download = `case-property-dashboard-${new Date()
      .toISOString()
      .slice(0, 10)}.csv`;
    document.body.appendChild(downloadLink);
    downloadLink.click();
    downloadLink.remove();
    URL.revokeObjectURL(downloadUrl);
  };

  const resetFilters = () => {
    setDistrictId("");
    setSubDivisionId("");
    setStationId("");
    setUserId("");
    setFilterOption("");
    setStartDate(null);
    setEndDate(null);
    localStorage.removeItem(REPORT_FILTER_OPTION_STORAGE_KEY);
    localStorage.removeItem(REPORT_HIERARCHY_SCOPE_STORAGE_KEY);
  };

  const hasActiveFilters = Boolean(
    districtId ||
      subDivisionId ||
      stationId ||
      userId ||
      filterOption ||
      startDate ||
      endDate
  );
  const showFixedStation =
    !canSelectStation && Boolean(fixedStationId) && stationLabel;

  const typeTableRows = typeMetrics.slice(0, 5).map((metric) => ({
    ...metric,
    key: metric.label,
  }));
  const reasonTableRows = reasonMetrics.map((metric) => ({
    ...metric,
    key: metric.label,
  }));

  return (
    <>
      <Navbar />
      <HelmetProvider>
        <Helmet>
          <title>{translatedText("dashboard_title")}</title>
        </Helmet>
      </HelmetProvider>

      <main
        aria-busy={dashboardLoading}
        className="dashboard-page"
      >
        {dashboardError ? (
          <div className="dashboard-notice" role="status">
            <FiAlertCircle />
            <span>{dashboardError}</span>
          </div>
        ) : null}

        <section
          aria-label={translatedText("case_property_summary")}
          className="dashboard-kpis"
        >
          <KpiCard
            accent={{ background: "#e9effc", foreground: "#164f93" }}
            context={translatedText("current_reporting_scope")}
            icon={FiArchive}
            info={`${translatedText("total_case_properties")} ${translatedText(
              "selected_scope_info"
            )}`}
            label={translatedText("total_case_properties")}
            lastMonthLabel={translatedText("last_month")}
            previousValue={lastMonthTotal}
            value={totalRecords}
          />
          <KpiCard
            accent={{ background: "#e9f5e8", foreground: "#27824b" }}
            context={translatedText("recorded_this_month")}
            icon={FiFilePlus}
            info={`${translatedText("added_this_month")} ${translatedText(
              "selected_scope_info"
            )}`}
            label={translatedText("added_this_month")}
            lastMonthLabel={translatedText("last_month")}
            value={addedThisMonth}
          />
          <KpiCard
            accent={{ background: "#fdeceb", foreground: "#b7353b" }}
            context={translatedText("disposed_during_this_month")}
            icon={FiCheckSquare}
            info={`${translatedText("disposed_this_month")} ${translatedText(
              "selected_scope_info"
            )}`}
            label={translatedText("disposed_this_month")}
            lastMonthLabel={translatedText("last_month")}
            value={disposedThisMonth}
          />
          <KpiCard
            accent={{ background: "#fff1df", foreground: "#bd690a" }}
            context={translatedText("properties_in_custody")}
            icon={MdBalance}
            info={`${translatedText(
              "balance_case_properties"
            )} ${translatedText("selected_scope_info")}`}
            label={translatedText("balance_case_properties")}
            lastMonthLabel={translatedText("last_month")}
            value={balanceProperties}
          />
        </section>

        <section
          aria-label={translatedText("dashboard_filters")}
          className="dashboard-filter-panel"
        >
          <div className="dashboard-filter-panel__controls">
            {showFixedStation ? (
              <div className="dashboard-fixed-scope">
                <span>{translatedText("police_station")}</span>
                <strong>{stationLabel}</strong>
              </div>
            ) : null}
            {canUseAdminHierarchy ? (
              <HierarchyFilterBar
                className="dashboard-hierarchy-filters"
                districtDisabled={!canSelectDistrict}
                districtOptions={districtFilterOptions}
                hideDateFilterLabel={false}
                onDateRangeChange={(dates) => {
                  const [nextStartDate, nextEndDate] = dates;
                  setStartDate(nextStartDate);
                  setEndDate(nextEndDate);
                }}
                onDistrictChange={(event) => {
                  setDistrictId(event.target.value);
                  setSubDivisionId("");
                  setStationId("");
                  setUserId("");
                }}
                onFilterOptionChange={(event) => {
                  setFilterOption(event.target.value);
                  if (event.target.value !== "custom") {
                    setStartDate(null);
                    setEndDate(null);
                  }
                }}
                onReset={resetFilters}
                onStationChange={(event) => {
                  setStationId(event.target.value);
                  setUserId("");
                }}
                onSubDivisionChange={(event) => {
                  setSubDivisionId(event.target.value);
                  setStationId("");
                  setUserId("");
                }}
                onUserChange={(event) => setUserId(event.target.value)}
                quickFilterOptions={DATE_FILTER_OPTIONS.map((option) => ({
                  value: option.value,
                  label: translatedText(option.labelKey),
                }))}
                selectedDistrictId={
                  canSelectDistrict ? districtId : fixedDistrictId
                }
                selectedDistrictLabel={districtLabel}
                selectedEndDate={endDate}
                selectedFilterOption={filterOption}
                selectedStartDate={startDate}
                selectedStationId={
                  canSelectStation ? stationId : fixedStationId
                }
                selectedStationLabel={stationLabel}
                selectedSubDivisionId={
                  canSelectSubDivision
                    ? subDivisionId
                    : fixedSubDivisionId
                }
                selectedSubDivisionLabel={subDivisionLabel}
                selectedUserId={userId}
                showDateFilters
                showDistrictFilter={showDistrictField}
                showReset={hasActiveFilters}
                showStationFilter={showStationField}
                showSubDivisionFilter={showSubDivisionField}
                showUserFilter={
                  canUseAdminHierarchy &&
                  (userOptions.length > 0 || Boolean(userId))
                }
                stationDisabled={!canSelectStation}
                stationOptions={stationFilterOptions}
                subDivisionDisabled={!canSelectSubDivision}
                subDivisionOptions={subDivisionFilterOptions}
                userOptions={userOptions}
                translate={translatedText}
              />
            ) : (
              <div className="dashboard-filter-summary">
                <FiCalendar />
                <span>{translatedText("showing_all_records")}</span>
              </div>
            )}
          </div>
          <button
            className="dashboard-export"
            onClick={handleExport}
            type="button"
          >
            <FiDownload />
            {translatedText("export_report")}
          </button>
        </section>

        {dashboardLoading ? (
          <div className="dashboard-loading" role="status">
            <span />
            {translatedText("updating_dashboard")}
          </div>
        ) : null}

        {canReadReports ? (
          <>
            <EntryTypeCardGrid
              entries={entryTypeMetrics}
              onEntryClick={handleEntryTypeClick}
              title={translatedText("case_property_by_entry_type")}
            />
            <EntryTypeCardGrid
              entries={vehicleEntryTypeMetrics}
              onEntryClick={handleEntryTypeClick}
              title={translatedText("vehicle_by_entry_type")}
            />
          </>
        ) : null}

        <div className="dashboard-analytics-grid">
          <DonutPanel
            action={() => navigate("/datalist")}
            actionLabel={translatedText("view_register")}
            centerLabel={translatedText("total")}
            info={`${translatedText(
              "status_of_muddamaal"
            )}: ${translatedText("status_distribution_info")}`}
            metrics={statusMetrics}
            onMetricClick={
              canReadAdminReports
                ? (metric) =>
                    handleMetricClick("status_of_muddamaal", metric)
                : null
            }
            title={translatedText("case_property_by_status")}
            total={statusTotal || totalRecords}
          />
          <DonutPanel
            action={() => navigate("/datalist")}
            actionLabel={translatedText("view_register")}
            centerLabel={translatedText("properties")}
            info={`${translatedText(
              "muddamaal_type"
            )}: ${translatedText("type_distribution_info")}`}
            metrics={typeMetrics}
            onMetricClick={
              canReadAdminReports
                ? (metric) => handleMetricClick("muddamaal_type", metric)
                : null
            }
            title={translatedText("case_property_by_type")}
            total={typeTotal || totalRecords}
            variant="type"
          />

          <section className="dashboard-panel dashboard-panel--court">
            <PanelHeader
              action={() => navigate("/View-Date")}
              actionLabel={translatedText("view_all_court_dates")}
              info={translatedText("nearest_upcoming_dates_info")}
              title={translatedText("upcoming_court_dates")}
            />
            {upcomingCourtDates.length ? (
              <div className="dashboard-court-list">
                {upcomingCourtDates.map((item) => (
                  <article className="dashboard-court-item" key={item.key}>
                    <time
                      className="dashboard-court-date"
                      dateTime={new Date(item.date).toISOString()}
                    >
                      <strong>
                        {new Intl.DateTimeFormat("en-GB", {
                          day: "2-digit",
                        }).format(item.courtDate)}
                      </strong>
                      <span>
                        {new Intl.DateTimeFormat("en-GB", {
                          month: "short",
                        })
                          .format(item.courtDate)
                          .toUpperCase()}
                      </span>
                      <small>{item.courtDate.getFullYear()}</small>
                    </time>
                    <div className="dashboard-court-item__content">
                      <strong>
                        {item.reference} / {translatedText("property_id")}:{" "}
                        {item.propertyId}
                      </strong>
                      <span title={item.description}>
                        {item.description}
                      </span>
                      <em
                        className={
                          item.typeKey === "hearing"
                            ? "dashboard-tag dashboard-tag--hearing"
                            : "dashboard-tag"
                        }
                      >
                        {item.type}
                      </em>
                    </div>
                  </article>
                ))}
              </div>
            ) : (
              <EmptyPanel>
                {translatedText("no_upcoming_dates")}
              </EmptyPanel>
            )}
          </section>
        </div>

        <div className="dashboard-summary-grid">
          <CompactTable
            action={() => navigate("/datalist")}
            actionLabel={translatedText("view_all")}
            columns={[
              {
                key: "label",
                label: translatedText("muddamaal_type"),
                render: (row) => (
                  <button
                    className="dashboard-table__metric"
                    disabled={
                      !canReadAdminReports ||
                      row.aggregate ||
                      row.value <= 0
                    }
                    onClick={() =>
                      handleMetricClick("muddamaal_type", row)
                    }
                    type="button"
                  >
                    <span
                      aria-hidden="true"
                      style={{ backgroundColor: row.color }}
                    />
                    {row.label}
                  </button>
                ),
              },
              {
                key: "value",
                label: translatedText("count"),
                render: (row) => <strong>{formatCount(row.value)}</strong>,
              },
            ]}
            emptyMessage={translatedText("no_type_data")}
            rows={typeTableRows}
            title={translatedText("top_5_property_types")}
            variant="metric"
          />

          <CompactTable
            action={() => navigate("/datalist")}
            actionLabel={translatedText("view_all")}
            columns={[
              {
                key: "label",
                label: translatedText("pending_follow_up_reason"),
                render: (row) => (
                  <button
                    className="dashboard-table__metric"
                    disabled={
                      !canReadAdminReports ||
                      row.aggregate ||
                      row.value <= 0
                    }
                    onClick={() =>
                      handleMetricClick("vehicle_reason", row)
                    }
                    type="button"
                  >
                    {row.label}
                  </button>
                ),
              },
              {
                key: "value",
                label: translatedText("count"),
                render: (row) => <strong>{formatCount(row.value)}</strong>,
              },
            ]}
            emptyMessage={translatedText("no_pending_reasons")}
            rows={reasonTableRows}
            title={translatedText("cases_pending_action")}
            variant="metric"
          />

          <CompactTable
            action={() => navigate("/datalist")}
            actionLabel={translatedText("view_register")}
            columns={[
              {
                key: "propertyId",
                label: translatedText("property_id"),
                render: (row) => <strong>{row.propertyId}</strong>,
              },
              { key: "action", label: translatedText("action") },
              {
                key: "date",
                label: translatedText("date_time"),
                render: (row) => {
                  const formattedDate = formatDateTime(
                    row.date,
                    "en-GB"
                  );
                  return (
                    <span className="dashboard-table__date">
                      {formattedDate.date}
                      {formattedDate.time
                        ? `, ${formattedDate.time}`
                        : ""}
                    </span>
                  );
                },
              },
              { key: "by", label: translatedText("by") },
            ]}
            emptyMessage={translatedText("no_recent_movements")}
            rows={recentMovements}
            title={translatedText("recent_property_movements")}
            variant="movements"
          />
        </div>
      </main>
    </>
  );
}
